import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, RotateCcw, User } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';

interface RevisionItem {
  id: number;
  label: string | null;
  created_at: string;
  user: { id: number; name: string } | null;
}

export function RevisionsDialog({
  pageId, open, onOpenChange, onRestore,
}: {
  pageId: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onRestore?: () => void;
}) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['page-revisions', pageId],
    queryFn: () => unwrap<RevisionItem[]>(api.get(`/pages/${pageId}/revisions`)),
    enabled: Boolean(pageId) && open,
  });

  const restore = useMutation({
    mutationFn: (revisionId: number) =>
      api.post(`/pages/${pageId}/revisions/${revisionId}/restore`),
    onSuccess: () => {
      toast.success('Versión restaurada');
      void qc.invalidateQueries({ queryKey: ['page', pageId] });
      void qc.invalidateQueries({ queryKey: ['page-revisions', pageId] });
      onRestore?.();
      onOpenChange(false);
    },
    onError: () => toast.error('No se pudo restaurar'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Historial de revisiones</DialogTitle>
          <DialogDescription>
            Cada vez que guardas se crea una versión automática. Puedes restaurar cualquier momento anterior.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {!data && <div className="py-8 text-center text-sm text-neutral-400">Cargando…</div>}
          {data?.length === 0 && (
            <div className="py-8 text-center text-sm text-neutral-400">
              Aún no hay revisiones. Las verás aparecer cuando guardes cambios.
            </div>
          )}
          {data?.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between rounded-md border border-neutral-200 p-3 hover:border-neutral-400"
            >
              <div className="flex items-center gap-3">
                <Clock className="size-4 text-neutral-400" />
                <div>
                  <div className="text-sm font-medium">
                    {new Date(r.created_at).toLocaleString('es')}
                    {r.label && <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-neutral-500">{r.label}</span>}
                  </div>
                  {r.user && (
                    <div className="flex items-center gap-1 text-xs text-neutral-500">
                      <User className="size-3" />
                      {r.user.name}
                    </div>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (confirm('¿Restaurar esta versión? Se creará una revisión automática del estado actual antes de cambiar.')) {
                    restore.mutate(r.id);
                  }
                }}
              >
                <RotateCcw className="size-3.5" /> Restaurar
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
